import { useJwt } from "@/providers";

type AuthMethod = "sms" | "otp" | null;

interface UseOtpAuthMethodResult {
  /**
   * Whether OTP verification is ready to use.
   * - true: SMS-based OTP is available, show normal OTP flow
   * - false: Authenticator app OTP is not ready yet, show warning message
   */
  isOtpReady: boolean;
  /**
   * The authentication method from the JWT's amr claim
   */
  authMethod: AuthMethod;
}

/**
 * Hook to determine if OTP verification is available based on environment and auth method.
 *
 * Logic:
 * - If NEXT_PUBLIC_BACKEND_PLATFORM is "uat" -> OTP is always ready
 * - If NEXT_PUBLIC_BACKEND_PLATFORM is "prd":
 *   - If amr includes "sms" -> OTP is ready (SMS-based verification)
 *   - If amr includes "otp" -> OTP is NOT ready (authenticator app not implemented yet)
 */
export function useOtpAuthMethod(): UseOtpAuthMethodResult {
  const { jwt } = useJwt();

  const platform = process.env.NEXT_PUBLIC_BACKEND_PLATFORM;
  const amr = jwt?.amr;

  // Determine the auth method from the amr claim
  let authMethod: AuthMethod = null;
  if (amr?.includes("sms")) {
    authMethod = "sms";
  } else if (amr?.includes("otp")) {
    authMethod = "otp";
  }

  // If not in production, OTP is always ready
  if (platform !== "production") {
    return {
      isOtpReady: true,
      authMethod,
    };
  }

  // In production, only SMS-based OTP is ready
  // Authenticator app OTP ("otp" in amr) is not ready yet
  const isOtpReady = authMethod === "sms";

  return {
    isOtpReady,
    authMethod,
  };
}
